import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ShieldCheck, ShieldX, AlertTriangle, CheckCircle, XCircle, HelpCircle, Lightbulb, FileText } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const CoverageLibrary = () => {
  const coverageScenarios = [
    {
      title: 'What\'s Typically Covered',
      icon: ShieldCheck,
      color: 'from-emerald-500 to-emerald-600',
      items: [
        {
          scenario: 'Fire Damage',
          explanation: 'Usually covers the direct heat, smoke and soot damage caused by a fire at your property. Often includes water damage and even the damage caused by firefighters as well.',
          covered: true
        },
        {
          scenario: 'Theft and Burglary',
          explanation: 'Contents stolen from your home during a break-in. You\'ll need to provide a police report and proof of ownership.',
          covered: true
        },
        {
          scenario: 'Storm and Water Damage',
          explanation: 'Damage from storms, rain, or burst pipes. Note: gradual water damage or poor maintenance may not be covered.',
          covered: true
        },
        {
          scenario: 'Accidental Damage (if included)',
          explanation: 'Unexpected damage like spilling wine on carpet or dropping your TV. Often an optional extra on your policy.',
          covered: true
        }
      ]
    },
    {
      title: 'Common Exclusions',
      icon: ShieldX,
      color: 'from-red-500 to-red-600',
      items: [
        {
          scenario: 'Age, Wear and Tear',
          explanation: 'Insurance is for unexpected mishaps life sometimes throws our way. It\'s not intended to fix things when they become worn out or no longer work due to age or normal use.',
          covered: false
        },
        {
          scenario: 'Gradual Water Damage',
          explanation: 'Slow leaks like a leaking shower base, deteriorating seals, or ongoing seepage. Insurance covers sudden, unexpected events, not gradual deterioration.',
          covered: false
        },
        {
          scenario: 'Unattended Property',
          explanation: 'Many policies exclude theft if your home is unattended for extended periods (e.g., 60+ days). Check your policy terms.',
          covered: false
        },
        {
          scenario: 'Deliberate Damage',
          explanation: 'Damage you cause intentionally, or damage caused by someone you invited into your home.',
          covered: false
        }
      ]
    },
    {
      title: 'Grey Areas - Check Your Policy',
      icon: AlertTriangle,
      color: 'from-amber-500 to-amber-600',
      items: [
        {
          scenario: 'Home Business Equipment',
          explanation: 'Standard contents policies may have limits on business equipment. You might need additional cover if you work from home.',
          covered: 'conditional'
        },
        {
          scenario: 'Items Away From Home',
          explanation: 'Some policies cover contents temporarily away (like luggage on holiday), but usually with lower limits. Others exclude this entirely.',
          covered: 'conditional'
        },
        {
          scenario: 'High-Value Items',
          explanation: 'Items over a certain value (often $1,000-$2,000) may need to be specifically listed with valuations provided.',
          covered: 'conditional'
        },
        {
          scenario: 'Flood Damage',
          explanation: 'Flood coverage varies significantly. Some policies include it, others exclude it, some offer it as an optional extra.',
          covered: 'conditional'
        }
      ]
    }
  ];

  const commonMistakes = [
    {
      mistake: 'Setting sum insured too low',
      consequence: 'You\'ll be out of pocket if you need to replace everything',
      solution: 'Do a room-by-room inventory and use replacement costs, not second-hand values'
    },
    {
      mistake: 'Not updating your policy',
      consequence: 'Major purchases may not be covered if your sum insured is too low',
      solution: 'Review and update your sum insured annually or after major purchases'
    },
    {
      mistake: 'No proof of ownership',
      consequence: 'Claims can be delayed or rejected without evidence you owned items',
      solution: 'Keep photos, receipts, and documentation (that\'s where <a href="https://verifaeye.com.au" target="_blank" rel="noopener noreferrer" class="text-cyan-600 hover:text-cyan-700 font-semibold">VerifAEye</a> helps!)'
    },
    {
      mistake: 'Assuming everything is covered',
      consequence: 'Surprises during claims when you find exclusions apply',
      solution: 'Read your policy document, especially the exclusions section'
    },
    {
      mistake: 'Not specifying high-value items',
      consequence: 'Items over policy limits may only be partially covered',
      solution: 'List valuable items separately with valuations where required'
    }
  ];

  const policyTips = [
    {
      tip: 'Read the Product Disclosure Statement (PDS)',
      detail: 'This document explains exactly what is and isn\'t covered. Yes, it\'s long, but the exclusions section is crucial.'
    },
    {
      tip: 'Understand your excess',
      detail: 'Know how much you\'ll pay out-of-pocket before insurance kicks in. Higher excess usually means lower premium.'
    },
    {
      tip: 'Ask about bundling discounts',
      detail: 'Combining contents, home, and car insurance with one insurer often gets you a discount.'
    },
    {
      tip: 'Review annually',
      detail: 'Your circumstances change - new purchases, lifestyle changes. Review your cover each year at renewal.'
    },
    {
      tip: 'Keep your insurer informed',
      detail: 'Major changes (renovations, running a business from home) should be disclosed to avoid claim issues.'
    }
  ];

  return (
    <section id="library" data-testid="coverage-library" className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge className="bg-cyan-100 text-cyan-700 hover:bg-cyan-200 px-4 py-1 text-sm">
            Educational Library
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900">
            Understanding Your Coverage
          </h2>
          <p className="text-xl text-slate-600">
            Learn what's typically covered, common exclusions, and how to avoid costly surprises
          </p>
        </div>

        {/* Coverage scenarios */}
        <div className="grid lg:grid-cols-3 gap-8 mb-20">
          {coverageScenarios.map((section, idx) => (
            <Card key={idx} className="border-2 border-slate-200 hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="bg-gradient-to-br from-slate-50 to-cyan-50">
                <div className={`w-14 h-14 bg-gradient-to-br ${section.color} rounded-xl flex items-center justify-center mb-4`}>
                  <section.icon className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl">{section.title}</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {section.items.map((item, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-start space-x-2">
                        {item.covered === true && <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />}
                        {item.covered === false && <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />}
                        {item.covered === 'conditional' && <HelpCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />}
                        <div>
                          <p className="font-semibold text-slate-900 text-sm">{item.scenario}</p>
                          <p className="text-slate-600 text-sm leading-relaxed">{item.explanation}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Common mistakes */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-slate-900 mb-3">Common Claim Mistakes to Avoid</h3>
            <p className="text-lg text-slate-600">Learn from others' experiences</p>
          </div>

          <Card className="border-2 border-slate-200 shadow-xl max-w-5xl mx-auto">
            <CardHeader className="bg-gradient-to-br from-red-50 to-amber-50">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-amber-500 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Avoid These Pitfalls</CardTitle>
                  <CardDescription className="text-base">
                    Simple steps to ensure smooth claims
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <Accordion type="single" collapsible className="w-full space-y-2">
                {commonMistakes.map((item, index) => (
                  <AccordionItem 
                    key={index} 
                    value={`mistake-${index}`}
                    className="border border-slate-200 rounded-lg px-4 hover:border-red-400 transition-colors"
                  >
                    <AccordionTrigger className="hover:no-underline py-4">
                      <span className="text-lg font-semibold text-slate-900 text-left">{item.mistake}</span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-4 pt-2 space-y-2">
                      <div className="flex items-start space-x-2">
                        <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-slate-700">Consequence:</p>
                          <p className="text-slate-600">{item.consequence}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-slate-700">Solution:</p>
                          <p className="text-slate-600">{item.solution}</p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>

        {/* Policy tips */}
        <div>
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-slate-900 mb-3">Smart Policy Tips</h3>
            <p className="text-lg text-slate-600">Get the most from your insurance</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {policyTips.map((item, index) => (
              <Card key={index} className="border-2 border-slate-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-2">{item.tip}</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">{item.detail}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Final disclaimer */}
        <div className="mt-16 max-w-4xl mx-auto bg-gradient-to-br from-slate-900 to-cyan-900 rounded-2xl p-8 text-white">
          <div className="flex items-start space-x-4">
            <FileText className="w-8 h-8 flex-shrink-0 mt-1" />
            <div className="space-y-2">
              <h4 className="text-xl font-bold">This is General Information Only</h4>
              <p className="text-slate-300 leading-relaxed">
                Insurance policies vary significantly between providers and products. This content provides general education 
                about common insurance concepts. Always read your specific policy documents (especially the Product Disclosure Statement) 
                and consult with your insurance broker or insurer for advice tailored to your circumstances.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoverageLibrary;
